// libraries
import * as Sentry from "@sentry/react";
import {
  indexedDBLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
} from "firebase/auth";
import { FirebaseOptions, initializeApp } from "firebase/app";
import {
  getStorage,
  connectStorageEmulator,
  FirebaseStorage,
} from "firebase/storage";
import {
  CACHE_SIZE_UNLIMITED,
  clearIndexedDbPersistence,
  connectFirestoreEmulator,
  Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
} from "firebase/firestore";

import { getProjectInfraSettings } from "./_projectInfraConstants";

// types & constants
import {
  WombatRegionCodes,
  WombatRegions,
} from "wombat-global/src/typings/multi-region";

const env = import.meta.env;

const config: FirebaseOptions = {
  apiKey: env.REACT_APP_API_KEY,
  appId: env.REACT_APP_APP_ID,
  authDomain: env.REACT_APP_AUTH_DOMAIN,
  databaseURL: env.REACT_APP_FIRESTORE_URL,
  measurementId: env.REACT_APP_MEASUREMENT_ID,
  projectId: env.REACT_APP_PROJECT_ID,
  storageBucket: env.REACT_APP_STORAGE_BUCKET,
};
if (env.REACT_APP_AUTH_DOMAIN?.includes("localhost")) {
  delete config.authDomain;
}

export const _firebase = initializeApp(config);

/**
 * auth setup
 */
export const AUTH_TOKEN_STORAGE = "AUTH_DECODED_TOKEN";
export const auth = getAuth(_firebase);

if (env.REACT_APP_AUTH_DOMAIN?.includes("localhost")) {
  connectAuthEmulator(auth, env.REACT_APP_AUTH_DOMAIN, {
    disableWarnings: true,
  });
}
void setPersistence(auth, indexedDBLocalPersistence).catch((error: unknown) => {
  Sentry.captureException(error, {
    tags: { firebase_operation: "set-auth-persistence" },
  });
});

/**
 * firestore setup
 */
const _firestoreConfig = {
  ignoreUndefinedProperties: true,
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
    cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  }),
};

const regionProjectId = env.REACT_APP_FIRESTORE_URL?.includes("localhost")
  ? "local"
  : env.REACT_APP_PROJECT_ID;

export const firestoreRegionDb: Record<WombatRegionCodes, Firestore> = {
  us: initializeFirestore(
    _firebase,
    _firestoreConfig,
    getProjectInfraSettings(regionProjectId).us.firestoreId,
  ),
  ca: initializeFirestore(
    _firebase,
    _firestoreConfig,
    getProjectInfraSettings(regionProjectId).ca.firestoreId,
  ),
};

connectToEmulator();

/**
 * terminate, clear index cache, and then reinitialize firestore
 */
export async function reinitializeFirestore() {
  const regions: WombatRegionCodes[] = ["us", "ca"];
  await Promise.all(
    regions.map(async (region) => {
      const fb = firestoreRegionDb[region];
      await terminate(fb);
      await clearIndexedDbPersistence(fb);
      /**
       * We mutate the data here!!!
       */
      firestoreRegionDb[region] = initializeFirestore(
        _firebase,
        _firestoreConfig,
        getProjectInfraSettings(regionProjectId)[region].firestoreId,
      );
    }),
  );
  connectToEmulator();
}

function connectToEmulator() {
  if (env.REACT_APP_FIRESTORE_URL?.includes("localhost")) {
    const [, port = 8080] = env.REACT_APP_FIRESTORE_URL.split(":");
    connectFirestoreEmulator(firestoreRegionDb.us, "127.0.0.1", Number(port));
  }
}

/**
 * storage setup
 */
export const firebaseRegionStorage: Record<WombatRegionCodes, FirebaseStorage> =
  {
    us: getStorage(
      _firebase,
      getProjectInfraSettings(regionProjectId).us.bucketName,
    ),
    ca: getStorage(
      _firebase,
      getProjectInfraSettings(regionProjectId).ca.bucketName,
    ),
  };
export const storage = getStorage(_firebase, env.REACT_APP_STORAGE_BUCKET);

if (env.REACT_APP_STORAGE_URL?.includes("localhost")) {
  const [host = "localhost", port = 9199] =
    env.REACT_APP_STORAGE_URL.split(":"); // prettier-ignore
  connectStorageEmulator(firebaseRegionStorage.us, host, Number(port));
  connectStorageEmulator(firebaseRegionStorage.ca, host, Number(port));
}
